/**
 * Splash Screen Service - Handles email submissions and early access code validation
 */

import { apiRequest } from '../config/api.js'
import { handleApiError } from './errorHandler.js'

const EARLY_ACCESS_CODE_KEY = 'dietedics_early_access_code'

/**
 * Submit email address for future communications
 * @param {string} email - Email address to submit
 * @returns {Promise<Object>} Response from server
 */
export async function submitEmail(email) {
  try {
    const response = await apiRequest('/interest_submissions', {
      method: 'POST',
      body: JSON.stringify({
        interest_submission: {
          email: email
        }
      })
    })
    return response
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Validate early access code
 * @param {string} code - Early access code to validate
 * @returns {Promise<Object>} Validation response with valid boolean
 */
export async function validateEarlyAccessCode(code) {
  try {
    const response = await apiRequest('/early_access/validate', {
      method: 'POST',
      body: JSON.stringify({
        code: code
      })
    })
    return response
  } catch (error) {
    // Don't show error modal for invalid codes - let the component handle it
    // Only show modal for network/server errors
    if (error.status && error.status >= 500) {
      handleApiError(error)
    }
    throw error
  }
}

/**
 * Save early access code to localStorage
 * @param {string} code - Early access code to save
 */
export function saveEarlyAccessCode(code) {
  localStorage.setItem(EARLY_ACCESS_CODE_KEY, code)
}

/**
 * Get early access code from localStorage
 * @returns {string|null} Early access code or null
 */
export function getEarlyAccessCode() {
  return localStorage.getItem(EARLY_ACCESS_CODE_KEY)
}

/**
 * Check if user has early access (code exists in localStorage)
 * @returns {boolean} True if early access code exists
 */
export function hasEarlyAccess() {
  return getEarlyAccessCode() !== null
}

/**
 * Remove early access code from localStorage
 */
export function clearEarlyAccessCode() {
  localStorage.removeItem(EARLY_ACCESS_CODE_KEY)
}
